import { useEffect, useState } from "react";
import styled from "styled-components/macro";
import Cookies from "universal-cookie";
import Auth from "../../Auth";
import { sendAppEvent } from "../../Utils";
import SudowriteLogo from "../SudowriteLogo";
import { SudoButton } from "../../sudokit/components/SudoButton";
import {
  AUTHOR_NATION, CONFERENCE_COOKIE, CONFERENCE_PLACEMENT_COOKIE,
  CONFERENCE_COOKIE_OPTIONS, conferenceAcceptsEntries, normalizeConferencePlacement,
} from "../../../../src/shared/conferenceCampaign";

const Page = styled.div`
  --primary-cta: #6530d8;
  --primary-cta-hover: #5423bd;
  --primary-cta-active: #48209e;
  --cta-text-color: #fff;
  color: #302c1c; background: #fff; min-height: 100vh;
  display: flex; flex-direction: column; width: 100%; margin: 0; text-align: left; hyphens: none;
  font-family: Inter, sans-serif;
  * { box-sizing: border-box; }
  header, footer { flex-shrink: 0; background-repeat: no-repeat; background-size: cover; background-position: center; }
  header { background-image: url("/assets/conference/header-paint.webp"); }
  .header-content, .footer-content { max-width: 1120px; margin: auto; display: flex; justify-content: space-between; gap: 20px; }
  .header-content { min-height: 64px; align-items: flex-start; padding: 16px 20px; }
  header .brand {
    width: 150px; height: 24px; flex-shrink: 0; color: #383838;
    -webkit-mask-image: url("/assets/conference/wordmark.svg");
    mask-image: url("/assets/conference/wordmark.svg");
  }
  .event { font-size: 12px; text-align: right; line-height: 15px; color: #161616; }
  .event strong { display: block; font-weight: 500; }
  main { display: block; align-content: normal; min-height: 0; width: 100%; max-width: 610px; padding: 48px 20px 64px; margin: 0 auto; font-family: inherit; hyphens: none; text-align: left; }
  .welcome { text-align: center; }
  h1 { font: 400 50px/1 "GT Super Text", Georgia, serif; letter-spacing: -1px; max-width: 470px; margin: 0 auto 16px; text-align: center; }
  p { font-size: 16px; line-height: 1.5; margin: 0; }
  .intro { max-width: 450px; margin: 0 auto 28px; }
  a:focus-visible, button:focus-visible { outline: 3px solid #6530d8; outline-offset: 4px; }
  .terms { font-size: 11px; line-height: 18px; margin: 20px auto 0; color: #65614e; }
  .account { font-size: 12px; line-height: 1.6; margin-top: 14px; overflow-wrap: anywhere; }
  a { color: inherit; text-decoration: underline; text-underline-offset: 3px; }
  .error { color: #9c2436; font-size: 14px; margin-top: 15px; }
  .illustrations { display: grid; grid-template-columns: 191fr 285fr; align-items: start; gap: 16px; max-width: 492px; margin: 32px auto 0; }
  .illustrations img { display: block; width: 100%; height: auto; }
  .benefits { display: grid; gap: 16px; max-width: 452px; margin: 40px auto 0; padding: 0 0 0 32px; list-style: none; }
  .benefits li { display: grid; grid-template-columns: 32px minmax(0, 1fr); align-items: start; gap: 8px; }
  .benefits .check { display: block; margin-top: 1px; }
  .benefits h2 { font: 400 20px/1.25 "GT Super Text", Georgia, serif; margin: 0 0 4px; letter-spacing: -.3px; }
  .benefits p { font-size: 12px; line-height: 18px; color: #65614e; }
  footer { margin-top: auto; background-image: url("/assets/conference/footer-paint.webp"); font-size: 12px; line-height: 1.5; color: #161616; }
  .footer-content { min-height: 92px; padding: 4px 20px 28px; align-items: flex-start; }
  footer nav { display: flex; gap: 18px; }
  footer a { display: inline-flex; align-items: center; min-height: 44px; }
  @media(max-width: 480px) {
    .header-content { padding: 16px; gap: 12px; }
    .event { font-size: 11px; }
    main { padding: 32px 22px 40px; }
    h1 { font-size: clamp(34px, 9.5vw, 42px); line-height: 1.08; letter-spacing: -1px; }
    .intro { font-size: 15px; margin-bottom: 24px; }
    .terms { margin-top: 16px; }
    .illustrations { gap: 12px; margin-top: 24px; }
    .benefits { margin-top: 28px; padding-left: 0; }
    .footer-content { min-height: 72px; padding: 4px 16px 16px; gap: 12px; }
    footer nav { gap: 16px; }
  }
`;

const PlanButton = styled(SudoButton).attrs({
  className: "h-[50px] px-5 py-3 font-semibold before:rounded-[24px]",
})`
  && {
    isolation: isolate; width: 100%; max-width: 374px; height: 50px;
    border-radius: 24px; padding: 12px 20px; font: 600 16px/24px Inter, sans-serif;
    color: #fff; text-decoration: none;
  }
  &&::before { border-radius: 24px; }
`;

export default function AuthorNation({ user }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const placement = normalizeConferencePlacement(new URLSearchParams(window.location.search).get("utm_content"));
  const open = conferenceAcceptsEntries(AUTHOR_NATION.id);

  useEffect(() => {
    sendAppEvent("Conference - Landing Viewed", { campaign: AUTHOR_NATION.id, placement });
  }, [placement]);

  async function enter() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (user && !user.isAnonymous) {
        const result = await Auth.fetchWithIdToken("/api/conference/enroll", { campaign: AUTHOR_NATION.id, placement });
        if (!result.success) throw new Error(result.message);
        // Enrollment category records acquisition history. The app checks the
        // current payment gate, including later purchases and support overrides.
        window.location.href = "/app";
      } else {
        const cookies = new Cookies();
        cookies.set(CONFERENCE_COOKIE, AUTHOR_NATION.id, CONFERENCE_COOKIE_OPTIONS);
        cookies.set(CONFERENCE_PLACEMENT_COOKIE, placement, CONFERENCE_COOKIE_OPTIONS);
        window.location.href = `${AUTHOR_NATION.entryPath}&utm_content=${placement}`;
      }
    } catch (err) {
      setError(err.message || "We couldn't open signup. Please try again.");
      setBusy(false);
    }
  }

  return <Page>
    <header>
      <div className="header-content">
        <SudowriteLogo className="brand" size="x-large" />
        <div className="event"><strong>Author Nation 2026</strong>November 9–14</div>
      </div>
    </header>
    <main>
      <section className="welcome" aria-labelledby="conference-heading">
        <h1 id="conference-heading">Bring your next chapter to life.</h1>
        <p className="intro">Meet your AI writing partner. Talk through a plot twist, find the next line, and write a story you love.</p>
        {open ? <>
          <PlanButton variant="primary" onClick={enter} disabled={busy} aria-busy={busy}>{busy ? "Opening your account…" : "Choose your plan"}</PlanButton>
          <p className="terms">Sign up or log in, then choose a plan.<br />Standard monthly or annual pricing.</p>
        </> : <>
          <p className="intro">Author Nation signup has ended. Your next chapter is still waiting.</p>
          <PlanButton variant="primary" forwardedAs="a" href="/pricing">Explore Sudowrite’s plans</PlanButton>
        </>}
        {user && !user.isAnonymous && <p className="account">Signed in as {user.email}. <a href={`/logout?campaign=${AUTHOR_NATION.id}`}>Switch account</a><br />Already subscribed? We’ll take you to your account.</p>}
        {error && <p className="error" role="alert">{error}</p>}
      </section>
      <div className="illustrations" aria-hidden="true">
        <img src="/assets/conference/book-stack.webp" width="382" height="379" alt="" />
        <img src="/assets/conference/open-book.webp" width="570" height="351" alt="" />
      </div>
      <ul className="benefits" aria-label="A writing partner from first idea to final draft">
        <li><img className="check" src="/assets/conference/check-circle.svg" width="32" height="32" alt="" /><div><h2>Talk through a plot twist.</h2><p>Brainstorm with Chat, a writing partner that knows your story.</p></div></li>
        <li><img className="check" src="/assets/conference/check-circle.svg" width="32" height="32" alt="" /><div><h2>Find the words for it.</h2><p>Bring scenes to life with Muse, our AI built for fiction.</p></div></li>
        <li><img className="check" src="/assets/conference/check-circle.svg" width="32" height="32" alt="" /><div><h2>Make a good draft great.</h2><p>Get story-specific Feedback with Professional and Max plans.</p></div></li>
      </ul>
    </main>
    <footer><div className="footer-content"><a href="mailto:hi@sudowrite.com">Questions? Talk to us</a><nav aria-label="Legal"><a href="https://sudowrite.com/terms-and-conditions">Terms</a><a href="https://sudowrite.com/privacy-policy">Privacy</a></nav></div></footer>
  </Page>;
}
